import React, { Suspense, useEffect } from 'react'
import styled from 'styled-components'
import { Column, ResponsiveLayout } from 'components/common/layout'
import { WithHeaderFooter } from 'components/misc/with-header-footer'
import { GetServerSideProps } from 'next'
import { useStartupDetail } from 'apis/hooks'
import { StartupTableInfo } from 'components/startup/startup-table-info'
import { StartupBaseInfo } from 'components/startup/startup-base-info'
import {
  NotFoundContent,
  SectionTitle,
} from 'components/startup/startup-styles'
import { Seo } from 'components/common/seo'
import { StartupTab } from 'components/startup/startup-tab'
import { StartupDetailInfo } from 'components/startup/startup-detail-info'
import { analytics } from 'infra/analytics'
import { scrollEventTrack } from 'utils/handlers'
import { HOST_BASE_URL } from 'infra/constants'
import { NotFound } from 'components/misc/not-found'
import { viewStartup } from 'utils/handlers'
import { useIsDesktop } from 'utils/hooks'
import { StartupPositionList } from 'containers/startups/startup-position-list'

const StartupWrapper: React.FC<{ startupId: string }> = ({ startupId }) => (
  <Suspense>
    <Startup startupId={Number(startupId)} />
  </Suspense>
)

let viewedStartupId = 0

export const Startup: React.FC<{ startupId: number }> = ({ startupId }) => {
  const { data: startup, status } = useStartupDetail(startupId)
  const isDesktop = useIsDesktop()

  useEffect(() => {
    analytics.traffic('startup_detail')
    document.addEventListener('scroll', () =>
      scrollEventTrack('compDetail_jsu_scroll'),
    )
    return () =>
      document.removeEventListener('scroll', () =>
        scrollEventTrack('compDetail_jsu_scroll'),
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!startup || viewedStartupId === startupId) return
    viewStartup(startupId)
    viewedStartupId = startupId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startup])

  if (status === 404) return <NotFound />
  if (status === 201)
    return (
      <NotFound
        title='기업 정보가 비공개 상태입니다.'
        desc={`기업의 요청으로 계정을 휴면 상태로 변경했거나\n계약이 만료되었습니다.`}
      />
    )
  if (!startup) return null
  const { curr } = startup
  const SECTION_WIDTH = 700
  const totalAmount = curr.fundings
    ? curr.fundings.reduce((acc, item) => acc + (item.amount || 0), 0)
    : 0
  return (
    <>
      <Seo
        title={`${curr.name} 채용 | 그룹바이`}
        description={curr.briefIntro}
        url={`${HOST_BASE_URL}/startups/${startupId}`}
        image={curr.images[0]}
      />
      <WithHeaderFooter>
        <StartupLayout>
          <Section>
            <Column style={{ marginBottom: 24 }}>
              <StartupBaseInfo data={curr} />
            </Column>
            <StartupTableInfo
              data={{
                ...curr,
                funding:
                  curr.fundings && curr.fundings.length > 0
                    ? {
                        amount: totalAmount,
                        round: curr.fundings[0].round,
                        attractedAt: curr.fundings[0].attractedAt || '',
                      }
                    : undefined,
              }}
            />
            {!isDesktop && curr.positions.length > 0 && (
              <Column style={{ paddingTop: 40 }}>
                <SectionTitle>채용 중인 포지션</SectionTitle>
                <StartupPositionList
                  startupId={curr.id}
                  positions={curr.positions}
                  columns={1}
                  limit={2}
                />
              </Column>
            )}
            <StartupTab />
            <StartupDetailInfo data={curr} width={SECTION_WIDTH} />
          </Section>
          {isDesktop && (
            <StickyBox>
              {curr.positions.length > 0 ? (
                <StartupPositionListWrapper>
                  <StartupPositionList
                    startupId={curr.id}
                    positions={curr.positions}
                    columns={1}
                    limit={4}
                  />
                </StartupPositionListWrapper>
              ) : (
                <NotFoundContent>채용 준비중입니다.</NotFoundContent>
              )}
            </StickyBox>
          )}
        </StartupLayout>
      </WithHeaderFooter>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { startupId } = context.query
  return { props: { startupId } }
}

const StartupPositionListWrapper = styled(Column)`
  height: calc(100vh - 160px);
  justify-content: flex-start;
  overflow: hidden scroll;
  -ms-overflow-style: none; // IE and Edge
  scrollbar-width: none; // Firefox
  ::-webkit-scrollbar {
    display: none; // Chrome, Safari and Opera
  }
`

const StartupLayout = styled(ResponsiveLayout)`
  gap: 40px;
  margin-top: 40px;
  @media ${(p) => p.theme.deviceSize.desktop} {
    margin-top: 60px;
    display: flex;
    justify-content: space-between;
  }
`

const Section = styled.section`
  gap: 40px;
  margin-bottom: 120px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    width: 100%;
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    width: 700px;
  }
`

const StickyBox = styled(Column)`
  position: sticky;
  top: 120px;
  width: 340px;
  height: 100%;
  gap: 20px;
  padding-bottom: 120px;
`

export default StartupWrapper
